import { DecisionGuide } from '../DrawerFacetTypes';

function hasText(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Runtime guard for server-shaped decision_guide JSON.
 * Mirrors backend `sanitize_decision_guide` structure enough to safely render (no throws on .map / .toUpperCase).
 * Optional `templates.call_script` / `letter_template` must be strings when present so clipboard copy never receives objects.
 */
export function isRenderableDecisionGuide(g: DecisionGuide | null | undefined): g is DecisionGuide {
    if (g == null || typeof g !== 'object') return false;
    if (!hasText((g as DecisionGuide).calm_summary) || !hasText((g as DecisionGuide).safe_first_step)) return false;

    const facts = (g as DecisionGuide).facts;
    if (!Array.isArray(facts) || facts.length === 0) return false;
    for (const f of facts) {
        if (!f || typeof f !== 'object') return false;
        if (!hasText(f.label) || !hasText(f.value) || !hasText(f.evidence)) return false;
    }

    const unknowns = (g as DecisionGuide).unknowns;
    if (!Array.isArray(unknowns)) return false;
    for (const u of unknowns) {
        if (!u || typeof u !== 'object') return false;
        if (!hasText(u.label)) return false;
        if (u.why != null && u.why !== '' && !hasText(u.why)) return false;
    }

    const pathways = (g as DecisionGuide).pathways;
    if (!Array.isArray(pathways)) return false;
    const n = pathways.length;
    if (n < 3 || n > 4) return false;
    for (const p of pathways) {
        if (!p || typeof p !== 'object') return false;
        if (!hasText(p.id) || !hasText(p.label) || !hasText(p.outcome) || !hasText(p.avoid)) return false;
        if (!Array.isArray(p.steps) || p.steps.length === 0) return false;
        if (!p.steps.every((step) => hasText(step))) return false;
    }

    const disclaimer = (g as DecisionGuide).disclaimer;
    if (!hasText(disclaimer)) return false;

    const templates = (g as DecisionGuide).templates;
    if (templates === undefined || templates === null) {
        return true;
    }
    if (typeof templates !== 'object' || Array.isArray(templates)) {
        return false;
    }
    const raw = templates as Record<string, unknown>;
    for (const key of ['call_script', 'letter_template'] as const) {
        const v = raw[key];
        if (v !== undefined && v !== null && typeof v !== 'string') {
            return false;
        }
    }

    return true;
}
